export class F1EngineSound {
  constructor(options = {}) {
    // 🔊 How audio is provided:
    // - Preferred: options.audioData (ArrayBuffer from manifest)
    // - Or:       options.audioBuffer (decoded AudioBuffer)
    // - Fallback: options.audioUrl (fetch + decode)
    this.audioData = options.audioData || null; // ArrayBuffer
    this.audioBuffer = options.audioBuffer || null; // AudioBuffer
    this.audioUrl = options.audioUrl;

    // Gears and speeds (mph) – tune these
    this.gearMaxSpeeds = options.gearMaxSpeeds || [
      0, 50, 90, 130, 170, 200, 220, 240,
    ]; // 1–7
    this.maxGear = this.gearMaxSpeeds.length - 1;

    // Pitch tuning
    this.basePitch = options.basePitch ?? 0.6; // idle-ish pitch
    this.pitchRange = options.pitchRange ?? 1.4; // how much pitch rises with RPM
    this.minPlaybackRate = 0.5;
    this.maxPlaybackRate = 3.5;

    // Gear + RPM behaviour
    this.shiftRpmThreshold = options.shiftRpmThreshold ?? 0.96; // shift at 96% of gear
    this.shiftDuration = options.shiftDuration ?? 0.09; // seconds
    this.shiftDropFactor = options.shiftDropFactor ?? 0.72; // how much pitch drops on shift

    this.downshiftRpmThresholdOnGain =
      options.downshiftRpmThresholdOnGain ?? 0.25;
    this.downshiftRpmThresholdOnLoss =
      options.downshiftRpmThresholdOnLoss ?? 0.6;
    this.downshiftRpmThreshold = this.downshiftRpmThresholdOnGain;
    this.downshiftDuration = options.downshiftDuration ?? 0.07; // slightly quicker
    this.downshiftDropFactor = options.downshiftDropFactor ?? 0.55; // deeper dip

    // Internal state
    this.ctx = null;
    this.engineSource = null;
    this.engineGain = null;
    this.buffer = null;

    this.currentSpeed = 0; // mph (smoothed)
    this.targetSpeed = 0; // mph (what game passes in)
    this.speedSmoothing = 0.18;

    this.gear = 1;
    this.isShifting = false;
    this.shiftTimer = 0;
    this.shiftFromPitch = 1;
    this.shiftDropPitch = 1;
    this.shiftToPitch = 1;

    this.lastUpdateTime = null;
    this.running = false;
  }

  async init() {
    if (this.ctx) return; // already initialised

    this.ctx = new (window.AudioContext || window.webkitAudioContext)();

    // Prefer decoded buffer if already supplied
    if (this.audioBuffer instanceof AudioBuffer) {
      this.buffer = this.audioBuffer;
    } else if (this.audioData instanceof ArrayBuffer) {
      // Use ArrayBuffer from manifest
      this.buffer = await this._decodeArrayBuffer(this.audioData);
    } else if (this.audioUrl) {
      // Fallback: fetch from URL (old behaviour)
      this.buffer = await this._loadAudioBuffer(this.audioUrl);
    } else {
      throw new Error(
        "F1EngineSound: no audioData, audioBuffer or audioUrl provided"
      );
    }

    this._createEngineNode();
  }

  async _decodeArrayBuffer(arrayBuf) {
    // Defensive copy because decodeAudioData may detach the buffer in some browsers
    const copy = arrayBuf.slice(0);
    return await this.ctx.decodeAudioData(copy);
  }

  async _loadAudioBuffer(url) {
    const res = await fetch(url);
    const arrayBuf = await res.arrayBuffer();
    return await this._decodeArrayBuffer(arrayBuf);
  }

  _createEngineNode() {
    if (!this.ctx || !this.buffer) return;

    // Each start() needs a fresh BufferSource
    this.engineSource = this.ctx.createBufferSource();
    this.engineSource.buffer = this.buffer;
    this.engineSource.loop = true;

    this.engineGain = this.ctx.createGain();
    this.engineGain.gain.value = 0.25; // overall volume

    this.engineSource.connect(this.engineGain).connect(this.ctx.destination);
    this.engineSource.playbackRate.value = this.basePitch;
  }

  async start() {
    if (!this.ctx) {
      await this.init();
    }

    if (this.ctx.state === "suspended") {
      await this.ctx.resume();
    }

    if (this.running) return;

    if (!this.engineSource) {
      this._createEngineNode();
    }

    this.engineSource.start(0);
    this.running = true;
    this.lastUpdateTime = performance.now();
    this._loop();
  }

  stop() {
    if (this.engineSource && this.running) {
      try {
        this.engineSource.stop();
      } catch (_) {}
    }
    this.running = false;
    // 🔁 Allow clean restart later
    this.engineSource = null;
  }

  /**
   * Game calls this every frame: provide speed in mph
   */
  setSpeedMph(speedMph) {
    this.targetSpeed = Math.max(0, speedMph);
  }

  _loop = () => {
    if (!this.running) return;

    const now = performance.now();
    const dt = (now - this.lastUpdateTime) / 1000;
    this.lastUpdateTime = now;

    this.update(dt);
    requestAnimationFrame(this._loop);
  };

  update(dt) {
    if (!this.engineSource) return;

    // Smooth the incoming speed to avoid jittery pitch
    this.currentSpeed +=
      (this.targetSpeed - this.currentSpeed) * this.speedSmoothing;

    // Clamp speed to realistic range
    const maxPossibleSpeed = this.gearMaxSpeeds[this.maxGear];
    const clampedSpeed = Math.max(
      0,
      Math.min(this.currentSpeed, maxPossibleSpeed)
    );

    // Determine RPM (0–1) for current gear
    const maxSpeedThisGear = this.gearMaxSpeeds[this.gear] || maxPossibleSpeed;
    const rpmRaw = maxSpeedThisGear > 0 ? clampedSpeed / maxSpeedThisGear : 0;
    const rpm = Math.pow(Math.min(Math.max(rpmRaw, 0), 1), 1.3);

    // Gear shift logic (upshift)
    if (
      !this.isShifting &&
      rpm >= this.shiftRpmThreshold &&
      this.gear < this.maxGear
    ) {
      this._startShift("up", clampedSpeed);
    }

    // Downshift logic (on braking / slowing)
    this.downshiftRpmThreshold =
      this.targetSpeed > this.currentSpeed
        ? this.downshiftRpmThresholdOnGain
        : this.downshiftRpmThresholdOnLoss;
    if (
      !this.isShifting &&
      rpm <= this.downshiftRpmThreshold &&
      this.gear > 1
    ) {
      this._startShift("down", clampedSpeed);
    }

    const basePitchForRpm = this.basePitch + rpm * this.pitchRange;
    let finalPitch;

    if (this.isShifting) {
      this.shiftTimer -= dt;
      const duration =
        this.shiftDirection === "up"
          ? this.shiftDuration
          : this.downshiftDuration;

      let t = Math.max(0, Math.min(1, 1 - this.shiftTimer / duration));

      // First half = dip
      if (t < 0.35) {
        const k = t / 0.35;
        finalPitch = this._lerp(this.shiftFromPitch, this.shiftDropPitch, k);
      } else {
        // Second half = climb to new gear pitch
        const k = (t - 0.35) / 0.65;
        finalPitch = this._lerp(this.shiftDropPitch, this.shiftToPitch, k);
      }

      if (this.shiftTimer <= 0) {
        this.isShifting = false;
      }
    } else {
      finalPitch = basePitchForRpm;
    }

    finalPitch = Math.min(
      Math.max(finalPitch, this.minPlaybackRate),
      this.maxPlaybackRate
    );
    this.engineSource.playbackRate.value = finalPitch;
  }

  _startShift(direction, currentSpeed) {
    this.isShifting = true;

    const isUp = direction === "up";
    const duration = isUp ? this.shiftDuration : this.downshiftDuration;
    this.shiftTimer = duration;

    const currentMax = this.gearMaxSpeeds[this.gear];
    const rpmCurrent = Math.max(0, Math.min(1, currentSpeed / currentMax));
    const pitchCurrent = this.basePitch + rpmCurrent * this.pitchRange;

    const nextGear = isUp
      ? Math.min(this.gear + 1, this.maxGear)
      : Math.max(this.gear - 1, 1);

    const nextMax = this.gearMaxSpeeds[nextGear];
    const rpmNext = Math.max(0, Math.min(1, currentSpeed / nextMax));
    const pitchNext = this.basePitch + rpmNext * this.pitchRange;

    this.shiftFromPitch = pitchCurrent;

    if (isUp) {
      this.shiftDropPitch = pitchCurrent * this.shiftDropFactor;
    } else {
      // deeper drop for downshift "BRAAAP"
      this.shiftDropPitch = pitchCurrent * this.downshiftDropFactor;
    }

    this.shiftToPitch = pitchNext;
    this.shiftDirection = direction;

    this.gear = nextGear;
  }

  _lerp(a, b, t) {
    return a + (b - a) * t;
  }
}
